import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ClipboardList, LayoutGrid, List, Search, SlidersHorizontal } from "lucide-react";
import { apiRequest, parseJsonResponse } from "@/lib/queryClient";
import { ORDER_STATUSES } from "@shared/smb";
import { smbBoardResponseSchema, type SmbBoardQuery, type SmbBoardResponse } from "@shared/smb-board";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BoardColumns, BoardPager, BrandLocatorItem, OrdersTable, STAGE_LABELS } from "@/components/smb/PipelineBoardParts";

const MAIN_STATUSES = ORDER_STATUSES.filter((status) => status !== "failed" && status !== "cancelled");
export type BoardState = Required<Pick<SmbBoardQuery, "status" | "package" | "brand" | "test" | "q" | "view" | "sort" | "direction" | "pages" | "locatorPage">>;

const validStatus = (value: string | null) => value && (ORDER_STATUSES as readonly string[]).includes(value) ? value : "";
const validNumber = (value: string | null) => value && /^\d+$/.test(value) ? Math.min(10000, Math.max(1, Number(value))) : 1;

export function readBoardState(search: string): BoardState {
  const params = new URLSearchParams(search);
  const rawPages = params.get("pages");
  let pages = "";
  if (rawPages) {
    try {
      const parsed: unknown = JSON.parse(rawPages);
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        const safe = Object.fromEntries(Object.entries(parsed).filter(([key, value]) =>
          (ORDER_STATUSES as readonly string[]).includes(key) &&
          typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= 10000,
        ));
        pages = JSON.stringify(safe);
      }
    } catch {
      pages = "";
    }
  }
  return {
    status: validStatus(params.get("status")),
    package: (params.get("package") ?? "").slice(0, 160),
    brand: (params.get("brand") ?? "").slice(0, 160),
    test: ["0", "1"].includes(params.get("test") ?? "") ? params.get("test")! : "",
    q: (params.get("q") ?? "").trim().slice(0, 160),
    view: params.get("view") === "table" ? "table" : "board",
    sort: params.get("sort") === "cost" ? "cost" : "age",
    direction: params.get("direction") === "asc" ? "asc" : "desc",
    pages,
    locatorPage: validNumber(params.get("locatorPage")),
  };
}

export function withBoardParams(current: string, changes: Record<string, string | undefined>, resetPages = false): string {
  const params = new URLSearchParams(current);
  for (const [key, value] of Object.entries(changes)) {
    if (value) params.set(key, value);
    else params.delete(key);
  }
  if (resetPages) {
    params.delete("pages");
    params.delete("locatorPage");
  }
  return params.toString();
}

export function shouldPollDocument(visible: boolean): number | false {
  return visible ? 15000 : false;
}

export function selectedBoardStatuses(status: string, includeClosed: boolean): string[] {
  if (status) return [status];
  return includeClosed ? [...ORDER_STATUSES] : [...MAIN_STATUSES];
}

type Props = {
  search: string;
  setLocation: (to: string) => void;
  packages: Array<{ id: string; name: string }>;
  onCreate: () => void;
};

export function PipelineBoard({ search, setLocation, packages, onCreate }: Props) {
  const state = useMemo(() => readBoardState(search), [search]);
  const [queryDraft, setQueryDraft] = useState(state.q ?? "");
  const [visible, setVisible] = useState(typeof document === "undefined" || document.visibilityState === "visible");
  const [showClosed, setShowClosed] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);

  useEffect(() => { setQueryDraft(state.q ?? ""); }, [state.q]);
  useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const requestParams = new URLSearchParams();
  for (const key of ["status", "package", "brand", "test", "q", "view", "sort", "direction", "pages", "locatorPage"] as const) {
    const value = state[key];
    if (value !== "" && value !== undefined) requestParams.set(key, String(value));
  }
  const requestSearch = requestParams.toString();
  const boardQuery = useQuery<SmbBoardResponse>({
    queryKey: ["/api/admin/smb/board", requestSearch],
    queryFn: async () => smbBoardResponseSchema.parse(await parseJsonResponse(await apiRequest("GET", `/api/admin/smb/board?${requestSearch}`))),
    refetchInterval: shouldPollDocument(visible),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });

  const update = (key: string, value: string, resetPages = false) => {
    const next = withBoardParams(search, { [key]: value }, resetPages);
    setLocation(`/admin/smb${next ? `?${next}` : ""}`);
  };
  const setPage = (status: string, page: number) => {
    let pages: Record<string, number> = {};
    try { pages = state.pages ? JSON.parse(state.pages) as Record<string, number> : {}; } catch { pages = {}; }
    pages[status] = page;
    update("pages", JSON.stringify(pages));
  };
  const submitSearch = () => update("q", queryDraft.trim().slice(0, 160), true);
  const changeSort = (sort: "age" | "cost") => {
    const direction = state.sort === sort && state.direction === "desc" ? "asc" : "desc";
    const next = withBoardParams(search, { sort, direction });
    setLocation(`/admin/smb?${next}`);
  };

  const closedStatusSelected = state.status === "failed" || state.status === "cancelled";
  const includeClosed = showClosed || closedStatusSelected;
  const selected = selectedBoardStatuses(state.status, includeClosed);
  const stages = boardQuery.data?.stages.filter((stage) => selected.includes(stage.status)) ?? [];
  const total = boardQuery.data?.stages.reduce((count, stage) => count + stage.count, 0) ?? 0;
  const closedCount = boardQuery.data?.stages
    .filter((stage) => stage.status === "failed" || stage.status === "cancelled")
    .reduce((count, stage) => count + stage.count, 0) ?? 0;
  const lastUpdated = boardQuery.data?.generatedAt
    ? new Date(boardQuery.data.generatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return <div className="space-y-5">
    <header className="rounded-xl border bg-card px-4 py-5 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pipeline board</h1>
          <p className="mt-1.5 max-w-xl text-sm text-muted-foreground">Inspect order progress and find a brand. This view does not start pipeline work.</p>
          <p className="mt-2 max-w-2xl text-xs text-muted-foreground">Recorded cost is usage so far. The cap limits admission of new work, not final spend. In-flight work may exceed it.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="mr-1 hidden text-[10px] text-muted-foreground sm:inline">{lastUpdated ? `Updated ${lastUpdated}` : "Live board"}</span>
          <Button variant="outline" size="sm" onClick={onCreate}>New test order</Button>
          <a href="/admin/smb/packages" className="rounded-md px-2.5 py-2 text-xs font-medium text-primary hover:bg-muted/50">Packages</a>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 rounded-lg border bg-background px-3 py-2"><span className="font-mono text-xl font-medium tabular-nums">{total}</span><span className="text-[10px] uppercase tracking-[.12em] text-muted-foreground">tracked orders</span></div>
        <div className="flex items-center gap-2 rounded-lg border bg-background px-3 py-2"><span className="text-[11px] text-muted-foreground">{visible ? "Auto-refreshing every 15 sec" : "Paused while tab is hidden"}</span></div>
      </div>
    </header>

    <section className="rounded-xl border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2"><Search className="h-4 w-4 text-muted-foreground" /><h2 className="text-xs font-semibold tracking-wide">Find a brand</h2><span className="hidden text-[10px] text-muted-foreground sm:inline">Search name, URL, email or external ID</span></div>
        <Button variant="ghost" size="sm" onClick={() => setFiltersOpen(!filtersOpen)} className="h-7 text-[10px]"><SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />{filtersOpen ? "Hide filters" : "Filters"}</Button>
      </div>
      {filtersOpen && <div className="grid gap-2.5 p-3 sm:grid-cols-2 lg:grid-cols-[minmax(230px,1.6fr)_1fr_1fr_130px_auto]">
        <form className="flex gap-1.5" onSubmit={(event) => { event.preventDefault(); submitSearch(); }}>
          <span className="relative min-w-0 flex-1"><Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" /><Input aria-label="Find a brand" value={queryDraft} onChange={(event) => setQueryDraft(event.target.value)} placeholder="Brand, URL, email, external ID" className="h-9 bg-background pl-8 text-xs" /></span>
          <Button type="submit" size="sm" variant="outline" className="h-9 px-3 text-xs">Find</Button>
        </form>
        <select aria-label="Filter status" value={state.status} onChange={(event) => update("status", event.target.value, true)} className="h-9 rounded-md border bg-background px-2 text-xs text-foreground"><option value="">All stages</option>{ORDER_STATUSES.map((status) => <option key={status} value={status}>{STAGE_LABELS[status]}</option>)}</select>
        <select aria-label="Filter package" value={state.package} onChange={(event) => update("package", event.target.value, true)} className="h-9 rounded-md border bg-background px-2 text-xs text-foreground"><option value="">All packages</option>{packages.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
        <Input aria-label="Filter brand ID" value={state.brand} maxLength={160} placeholder="Brand ID" onChange={(event) => update("brand", event.target.value.slice(0, 160), true)} className="h-9 bg-background text-xs" />
        <select aria-label="Test order filter" value={state.test} onChange={(event) => update("test", event.target.value, true)} className="h-9 rounded-md border bg-background px-2 text-xs text-foreground"><option value="">Tests hidden</option><option value="1">Include tests</option><option value="0">Non-test only</option></select>
      </div>}
      {state.q && <div className="border-t px-4 py-2 text-[10px] text-muted-foreground">Submitted search: <strong className="font-medium text-foreground">{state.q}</strong></div>}
      {state.q && boardQuery.data && <div className="divide-y">
        {boardQuery.data.locator.items.length
          ? boardQuery.data.locator.items.map((item, index) => <BrandLocatorItem key={`${item.brandId ?? item.order?.id ?? "orphan"}-${index}`} item={item} />)
          : <div className="px-4 py-5 text-center text-xs text-muted-foreground">No matching brand or order. Try a name, URL, email or external ID.</div>}
        <BoardPager page={boardQuery.data.locator.page} count={boardQuery.data.locator.total} label="results" onChange={(page) => update("locatorPage", String(page))} />
      </div>}
    </section>

    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><div className="flex items-center gap-2"><span className="text-[10px] font-semibold uppercase tracking-[.17em] text-muted-foreground">Progression</span><span className="h-px w-10 bg-border" /><span className="text-[10px] text-muted-foreground">{state.status ? STAGE_LABELS[state.status] : "All active stages"}</span></div><h2 className="mt-1 text-lg font-semibold tracking-tight">The queue</h2></div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" disabled={closedStatusSelected} className="h-8 text-[11px]" onClick={() => setShowClosed(!showClosed)}>{closedStatusSelected ? "Closed stage selected" : showClosed ? "Hide failed & cancelled" : `Show failed & cancelled (${closedCount})`}</Button>
        <div className="flex rounded-lg border bg-muted/40 p-0.5">
          <button aria-label="Board view" aria-pressed={state.view === "board"} className={`rounded-md p-1.5 ${state.view === "board" ? "bg-background text-primary shadow-sm" : "text-muted-foreground"}`} onClick={() => update("view", "board")}><LayoutGrid className="h-3.5 w-3.5" /></button>
          <button aria-label="Table view" aria-pressed={state.view === "table"} className={`rounded-md p-1.5 ${state.view === "table" ? "bg-background text-primary shadow-sm" : "text-muted-foreground"}`} onClick={() => update("view", "table")}><List className="h-3.5 w-3.5" /></button>
          <button aria-label="Client intake view" title="Client intake" aria-pressed={false} className="rounded-md p-1.5 text-muted-foreground hover:bg-background hover:text-primary" onClick={() => update("view", "client-intake")}><ClipboardList className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </div>

    {boardQuery.isError
      ? <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5"><div className="flex items-center gap-2 text-sm font-medium text-destructive"><AlertTriangle className="h-4 w-4" />The board could not be loaded.</div><p className="mt-1 text-xs text-destructive/80">{boardQuery.error.message}</p><Button variant="outline" size="sm" className="mt-3" onClick={() => void boardQuery.refetch()}>Retry</Button></div>
      : boardQuery.isLoading
        ? <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"><div className="col-span-full h-24 animate-pulse rounded-xl bg-muted/60" />{[0, 1, 2, 3].map((index) => <div key={index} className="h-44 animate-pulse rounded-xl bg-muted/60" />)}</div>
        : boardQuery.data && (state.view === "board"
          ? <BoardColumns stages={stages} onPage={setPage} />
          : <OrdersTable stages={stages} sort={state.sort} direction={state.direction} onSort={changeSort} onPage={setPage} />)}
  </div>;
}
